import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import PropTypes from "prop-types";
import { useQuery, gql } from "@apollo/client";
import "./HeroBanner.css";
import chevronLeft from "../../assets/chevron-left.svg";
import chevronRight from "../../assets/chevron-right.svg";

const TOUCH_DEVICE_MEDIA_QUERY = "(max-width: 1024px)";
const SWIPE_THRESHOLD = 50;

const GET_HOME_BANNERS = gql`
  {
    getHomeBannersCached(device: WEB) {
      background_image
      link
      title
      subtitle
      description
    }
  }
`;

/**
 * 홈화면 배너 컴포넌트
 */
const HeroBanner = memo(function HeroBanner({ autoPlayInterval = 5000 }) {
  const { data } = useQuery(GET_HOME_BANNERS);

  const banners = data?.getHomeBannersCached ?? [];
  const hasBanners = banners.length > 0;
  const touchStartRef = useRef({ x: 0, y: 0 });
  const ignoreClickRef = useRef(false);

  // 무한 루프: [last, ...banners, first] 형태로 클론 추가
  // currentIndex 1 = 실제 첫 번째, banners.length = 실제 마지막
  const [currentIndex, setCurrentIndex] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);

  const hasMultiple = banners.length > 1;
  const displayBanners = hasMultiple
    ? [banners[banners.length - 1], ...banners, banners[0]]
    : banners;

  // 논리적 인덱스 (dots, 텍스트 표시용)
  const activeSlide = hasMultiple
    ? (currentIndex - 1 + banners.length) % banners.length
    : 0;

  const currentBanner = banners[activeSlide] || null;
  const currentBannerLink = currentBanner?.link?.trim();
  const isClickable = Boolean(currentBannerLink);
  const tag = currentBanner?.subtitle;
  const title = currentBanner?.title;
  const subtitle = currentBanner?.description;

  const autoPlayTimerRef = useRef(null);

  const startAutoPlay = useCallback(() => {
    if (autoPlayInterval <= 0 || !hasMultiple) return;
    clearInterval(autoPlayTimerRef.current);
    autoPlayTimerRef.current = setInterval(() => {
      if (document.hidden) return;
      setIsAnimating(true);
      setCurrentIndex((prev) => prev + 1);
    }, autoPlayInterval);
  }, [autoPlayInterval, hasMultiple]);

  const slideNext = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => prev + 1);
    startAutoPlay();
  }, [isAnimating, startAutoPlay]);

  const slidePrev = useCallback(() => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrentIndex((prev) => prev - 1);
    startAutoPlay();
  }, [isAnimating, startAutoPlay]);

  const goToSlide = useCallback(
    (logicalIndex) => {
      if (isAnimating) return;
      setIsAnimating(true);
      setCurrentIndex(logicalIndex + 1);
      startAutoPlay();
    },
    [isAnimating, startAutoPlay],
  );

  // 경계 클론에 도달하면 transition 없이 실제 위치로 점프
  const handleTransitionEnd = useCallback(() => {
    if (!hasMultiple) return;
    if (currentIndex === 0) {
      setIsAnimating(false);
      setCurrentIndex(banners.length);
    } else if (currentIndex === banners.length + 1) {
      setIsAnimating(false);
      setCurrentIndex(1);
    } else {
      setIsAnimating(false);
    }
  }, [currentIndex, banners.length, hasMultiple]);

  useEffect(() => {
    startAutoPlay();
    return () => clearInterval(autoPlayTimerRef.current);
  }, [startAutoPlay]);

  // 탭 전환 시 interval이 쌓여 currentIndex가 범위를 벗어나는 문제 방지
  const bannersLengthRef = useRef(banners.length);
  bannersLengthRef.current = banners.length;

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearInterval(autoPlayTimerRef.current);
      } else {
        const len = bannersLengthRef.current;
        if (len > 1) {
          // currentIndex를 유효 범위 [1, len]으로 복구
          setCurrentIndex((prev) => {
            if (prev < 1 || prev > len) return 1;
            return prev;
          });
        }
        setIsAnimating(false);
        startAutoPlay();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [startAutoPlay]);

  const handleDotClick = (index) => {
    goToSlide(index);
  };

  const handlePrev = useCallback(
    (event) => {
      event.stopPropagation();
      slidePrev();
    },
    [slidePrev],
  );

  const handleNext = useCallback(
    (event) => {
      event.stopPropagation();
      slideNext();
    },
    [slideNext],
  );

  const isTouchDeviceViewport = useCallback(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return false;
    }

    return window.matchMedia(TOUCH_DEVICE_MEDIA_QUERY).matches;
  }, []);

  const handleBannerClick = useCallback(() => {
    if (ignoreClickRef.current) {
      ignoreClickRef.current = false;
      return;
    }

    if (!currentBannerLink) {
      return;
    }

    try {
      const destination = new URL(
        currentBannerLink,
        window.location.origin,
      ).toString();
      window.location.assign(destination);
    } catch {
      window.location.assign(currentBannerLink);
    }
  }, [currentBannerLink]);

  const handleBannerKeyDown = useCallback(
    (event) => {
      if (!currentBannerLink || event.target !== event.currentTarget) {
        return;
      }

      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleBannerClick();
      }
    },
    [currentBannerLink, handleBannerClick],
  );

  const handleTouchStart = useCallback(
    (event) => {
      if (
        !isTouchDeviceViewport() ||
        !hasMultiple ||
        event.touches.length !== 1
      ) {
        return;
      }

      const touch = event.touches[0];
      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      ignoreClickRef.current = false;
    },
    [hasMultiple, isTouchDeviceViewport],
  );

  const handleTouchEnd = useCallback(
    (event) => {
      if (!isTouchDeviceViewport() || !hasMultiple) {
        return;
      }

      const touch = event.changedTouches[0];
      if (!touch) {
        return;
      }

      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const isHorizontalSwipe =
        Math.abs(deltaX) >= SWIPE_THRESHOLD &&
        Math.abs(deltaX) > Math.abs(deltaY);

      if (!isHorizontalSwipe) {
        return;
      }

      ignoreClickRef.current = true;

      if (deltaX < 0) {
        slideNext();
        return;
      }

      slidePrev();
    },
    [hasMultiple, slideNext, slidePrev, isTouchDeviceViewport],
  );

  if (!hasBanners) {
    return (
      <section className="hero-banner hero-banner-placeholder" aria-hidden="true">
        <div className="hero-banner-overlay" />
      </section>
    );
  }

  return (
    <section
      className={`hero-banner${isClickable ? " hero-banner-clickable" : ""}`}
      onClick={handleBannerClick}
      onKeyDown={handleBannerKeyDown}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role={isClickable ? "link" : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      {/* Carousel Images */}
      <div
        className="hero-banner-carousel"
        style={{
          transform: `translateX(-${currentIndex * 100}%)`,
          transition: isAnimating
            ? "transform 0.6s cubic-bezier(0.25, 0.1, 0.25, 1)"
            : "none",
        }}
        onTransitionEnd={handleTransitionEnd}
      >
        {displayBanners.map((banner, index) => {
          const isClone = index === 0 || index === displayBanners.length - 1;
          const isFirst = index === 1;
          return (
            <img
              key={index}
              src={banner.background_image}
              alt={banner.title || `Banner ${index + 1}`}
              aria-hidden={index !== currentIndex}
              loading={isClone || isFirst ? "eager" : "lazy"}
              fetchPriority={isFirst ? "high" : "auto"}
            />
          );
        })}
      </div>

      {/* Arrow Navigation */}
      {hasMultiple && (
        <>
          <button
            type="button"
            className="hero-banner-arrow hero-banner-arrow-prev"
            onClick={handlePrev}
            aria-label="Previous slide"
          >
            <img src={chevronLeft} alt="" width="24" height="24" />
          </button>
          <button
            type="button"
            className="hero-banner-arrow hero-banner-arrow-next"
            onClick={handleNext}
            aria-label="Next slide"
          >
            <img src={chevronRight} alt="" width="24" height="24" />
          </button>
        </>
      )}

      {/* Gradient Overlay */}
      <div className="hero-banner-overlay" />

      {/* Content */}
      <div className="hero-banner-content">
        {/* Dots Navigation */}
        {hasMultiple && (
          <div className="hero-banner-dots">
            {banners.map((_, index) => (
              <button
                type="button"
                key={index}
                className={`hero-banner-dot ${index === activeSlide ? "active" : ""}`}
                onClick={(event) => {
                  event.stopPropagation();
                  handleDotClick(index);
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
        {tag && <div className="hero-banner-tag">{tag}</div>}
        {title && (
          <h2 className="hero-banner-title">
            {title.split(/<br\s*\/?>/i).map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </h2>
        )}
        {subtitle && <p className="hero-banner-subtitle">{subtitle}</p>}
      </div>
    </section>
  );
});

HeroBanner.propTypes = {
  autoPlayInterval: PropTypes.number,
};

export default HeroBanner;
